// Domain types & enums — single source of truth.
// Mirrors docs/prd/04-data-model.md (grounded in the real Airtable snapshot).
// Values below are VERBATIM from the data; keep them exact (spaces, casing, punctuation).
import { z } from "zod";

// ── Domains (5 real codes, from Domain Performance) ──────────────────────────
export const DOMAINS = ["BCS", "MedSci", "PH", "PSE", "SSH"] as const;
export type Domain = (typeof DOMAINS)[number];
export const DomainSchema = z.enum(DOMAINS);

/** Human-readable expansions — TODO(open-input): confirm BCS/PSE/SSH with the team (spec §12). */
export const DOMAIN_LABELS: Record<Domain, string> = {
  BCS: "Biological & Chemical Sciences",
  MedSci: "Medical Sciences",
  PH: "Public Health",
  PSE: "Physical Sciences & Engineering",
  SSH: "Social Sciences & Humanities",
};

/** The raw `Users.Domain` / `Selected.Domain` multi-select carries non-canonical values
 *  ("Data Science", "Don't Know/Undecided"). Coerce to the 5 canonical codes; drop the rest. */
export function coerceDomain(value: unknown): Domain | null {
  if (typeof value !== "string") return null;
  return (DOMAINS as readonly string[]).includes(value) ? (value as Domain) : null;
}

// ── Selection lifecycle: Selected.Status ─────────────────────────────────────
// July-2 iteration (FR-J4): selection is DIRECT + AE-gated — `To Pitch(Editorial) → Selected`
// in one step; the earlier `Proposed for Selection` two-step is removed.
export const SELECTED_STATUSES = [
  "Unread",
  "Taken",
  "To Pitch(Team)",
  "Team Rejected", // rejected at the domain team-meeting pitch gate (Softr "Change Status")
  "To Pitch(Editorial)",
  "Editorial Rejected", // rejected at the editorial-meeting pitch gate
  "Selected",
] as const;
export type SelectedStatus = (typeof SELECTED_STATUSES)[number];
export const SelectedStatusSchema = z.enum(SELECTED_STATUSES);

/** Human-facing labels (July-8 Bucket 2) — API/Airtable values stay verbatim. */
export const SELECTED_STATUS_LABELS: Partial<Record<SelectedStatus, string>> = {
  Unread: "Unclaimed",
  Taken: "Claimed",
};

export function selectedStatusLabel(status: SelectedStatus): string {
  return SELECTED_STATUS_LABELS[status] ?? status;
}

/** Short card preview from a full abstract (discovery cards — not the full text). */
export function abstractBlurb(abstract: string | null | undefined, maxLen = 220): string | null {
  if (!abstract?.trim()) return null;
  const flat = abstract.trim().replace(/\s+/g, " ");
  if (flat.length <= maxLen) return flat;
  const cut = flat.slice(0, maxLen).replace(/\s+\S*$/, "");
  return `${cut}…`;
}

/** Selected.Proposal Status (Prophy reviewer-suggestion generation). */
export const PROPOSAL_STATUSES = [
  "Created",
  "Refined",
  "Failed (Bad PDF Link)",
  "Failed (API Error)",
  "None",
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const ProposalStatusSchema = z.enum(PROPOSAL_STATUSES);

// ── Reviewer-pipeline lifecycle: Preprint Info ONLY.Preprint Status ──────────
export const PREPRINT_STATUSES = [
  "ACTIVE",
  "ON HOLD",
  "DORMANT",
  "INVITED",
  "COMPLETE",
  "DISCARDED",
] as const;
export type PreprintStatus = (typeof PREPRINT_STATUSES)[number];
export const PreprintStatusSchema = z.enum(PREPRINT_STATUSES);

/** Preprint Info ONLY.Reviewer Outreach Status (publish sub-stage). */
export const OUTREACH_STATUSES = [
  "Reviewer Selection in Process",
  "Zero Acceptances",
  "1 Review Accepted",
  "1 Review Published",
  "Published",
] as const;
export type OutreachStatus = (typeof OUTREACH_STATUSES)[number];

// ── Reviewer proposal status: Student Reviewer Inputs.Status ─────────────────
// July-2 iteration (FR-J13) trims the original 15 snapshot values: the H-index gates
// (Low/Extremely Low/High) are auto-captured at COI time, `Incomplete` is gone (proposals are
// complete on submit — FR-J12), the old `Invite Backup` is folded into `Invite Backup Unchecked`,
// and `Tentative Invite` is deduped into `Double Check`. `COI Detected` is KEPT: the async
// Prophy COI seam (wall 4) can still produce it until the real engine replaces the stub.
// Legacy snapshot values are coerced at the airtable-sync boundary (LEGACY_REVIEWER_STATUSES).
export const REVIEWER_STATUSES = [
  "Unchecked",
  "Invite",
  "Reject",
  "Invite Backup Unchecked",
  "COI Detected",
  "Fixable",
  "Fixed",
  "Student Reject",
  "Double Check",
] as const;
export type ReviewerStatus = (typeof REVIEWER_STATUSES)[number];
export const ReviewerStatusSchema = z.enum(REVIEWER_STATUSES);

/** Retired snapshot statuses → their July-2 replacement (applied at import/sync time). */
export const LEGACY_REVIEWER_STATUSES: Record<string, ReviewerStatus> = {
  "Tentative Invite": "Double Check",
  "Invite Backup": "Invite Backup Unchecked",
  Incomplete: "Unchecked",
  "Low H-index": "Unchecked",
  "Extremely Low H-index": "Unchecked",
  "High H-index": "Unchecked",
};

/** Final Status collapses the 15 to three for reporting. */
export const REVIEWER_FINAL_STATUSES = ["Unchecked", "Reject", "Invite"] as const;
export type ReviewerFinalStatus = (typeof REVIEWER_FINAL_STATUSES)[number];

// ── Candidate pool status: Referees.Status ───────────────────────────────────
export const REFEREE_STATUSES = [
  "Unclaimed",
  "Proposed",
  "Claimed",
  "Reject",
  "Unable to Evaluate",
] as const;
export type RefereeStatus = (typeof REFEREE_STATUSES)[number];
export const RefereeStatusSchema = z.enum(REFEREE_STATUSES);

// ── COI (performed by Prophy) ────────────────────────────────────────────────
export const COI_STATUSES = ["Prophy Checked", "Not Checked"] as const;
export type CoiStatus = (typeof COI_STATUSES)[number];

/** COI engine verdict (our normalized result). */
export const COI_VERDICTS = ["clear", "conflict"] as const;
export type CoiVerdict = (typeof COI_VERDICTS)[number];

/** The four conflict reasons enumerated in COI-4 / FR-2.10. */
export const COI_REASONS = [
  "already-proposed",
  "co-authorship",
  "is-author",
  "same-institution",
] as const;
export type CoiReason = (typeof COI_REASONS)[number];

// ── App-login roles (real values from Users.Role / Users."Role- Reviewer Selection") ─
// "Reviewer" = external invitee (no app login in v1); "Hold" = deactivated — both excluded.
export const ROLES = [
  "Student",
  "Quality Checker in Training",
  "Manager",
  "Central",
  "Admin",
] as const;
export type Role = (typeof ROLES)[number];
export const RoleSchema = z.enum(ROLES);

/** Maps a raw Users.Role / Role- Reviewer Selection value to an app role, or null if excluded. */
export function coerceRole(value: unknown): Role | null {
  if (typeof value !== "string") return null;
  if ((ROLES as readonly string[]).includes(value)) return value as Role;
  // Reviewer / Hold / Dropped etc. → no app login.
  return null;
}

/** Proposer Role provenance (audit) on Student Reviewer Inputs. */
export const PROPOSER_ROLES = ["Student", "Manager", "Quality Checker in Training", "Central"] as const;
export type ProposerRole = (typeof PROPOSER_ROLES)[number];

/** Proposal Source provenance. */
export const PROPOSAL_SOURCES = ["Student", "Prophy"] as const;
export type ProposalSource = (typeof PROPOSAL_SOURCES)[number];

// H-index auto-gate statuses were removed in the July-2 iteration (FR-J13): H-index is
// auto-captured at COI time instead of driving dedicated reviewer statuses.
